import * as BABYLON from 'babylonjs';
import EnvConfig from '../envConfig';

export default class CoreUtils
{

static NUMBERS_REGEX = /^(\d*(\.\d*)?)?$/g;
static REAL_NUMBERS_REGEX = /^(-?\d*(\.\d*)?)?$/g;

static parseStringToDecimal(input) {
	const numberMatches = input.match(this.REAL_NUMBERS_REGEX);
	if(!numberMatches) {
		return null;
	}
	
	return numberMatches[0];
}

/*****************************************************************************/
static AnimationType =
{
	LINEAR:			0,
	EASE_IN_OUT:	1
};
/*****************************************************************************/
static getIsRunningOnOSX()
{
    return (window.navigator.userAgent.indexOf("Mac") !== -1);
}
/*****************************************************************************/
static createGuid()
{
    function s4()
    {
        return Math.floor((1 + Math.random()) * 0x10000)
		  .toString(16)
		  .substring(1);
    }
    return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
	  s4() + '-' + s4() + s4() + s4();
}
/*****************************************************************************/
static deepCopyString(strValue)
{
	return (' ' + strValue).slice(1);
}
/*****************************************************************************/
static clamp(value, min, max)
{
    return Math.max( min, Math.min( max, value ) );
}
/*****************************************************************************/
static shorten(strValue, iMaxChars)
{
	if(!strValue)
		return;

	if(strValue.length > iMaxChars)
		strValue = strValue.substr(0, iMaxChars) + '...';
	return strValue;
}
/*****************************************************************************/
// Be careful when using this. If an object being cloned has pointers to other
// objects which, in turn, have pointers to it, we will get into an infinite loop.
static clone(obj, optExcludedAttrs, bKeepBabylonClasses)
{
	let copy;

	// Handle the 3 simple types, and null or undefined
	if(null === obj || "object" !== typeof obj)
		return obj;

	// Handle Date
	if(obj instanceof Date)
	{
		copy = new Date();
		copy.setTime(obj.getTime());
		return copy;
	}

	// Handle Array
	if(obj instanceof Array)
	{
		copy = [];
		for(let i = 0, len = obj.length; i < len; i++)
			copy[i] = CoreUtils.clone(obj[i], optExcludedAttrs, bKeepBabylonClasses);
		return copy;
	}

	if(bKeepBabylonClasses)
	{
		if(obj.clone)
			return obj.clone();
	}

	// Handle Object
	if(obj instanceof Object)
	{
		copy = {};
		for(let attr in obj)
		{
			if(optExcludedAttrs && optExcludedAttrs.indexOf(attr) >= 0)
				continue;

			if(obj.hasOwnProperty(attr))
			{
				// In newer versions of BJS, they renamed properties like '.x' to '._x', and
				// provided setters/getters named x(). However, since those are getters/setters,
				// we don't copy them here. Thus, we check if the current property starts with
				// an underscore, and if so, we check if it has a getter that evaluates to the
				// same value. If that's the case, we use that name instead:
				let copiedAttrName = attr;
				if(attr[0] === '_' && obj[attr.substr(1)] === obj[attr])
					copiedAttrName = attr.substr(1);
				copy[copiedAttrName] = CoreUtils.clone(obj[attr], optExcludedAttrs, bKeepBabylonClasses);
			}
		}
		return copy;
	}

	throw new Error("Unable to copy obj! Its type isn't supported.");
}
/*****************************************************************************/
static saveFile(content, fileName, contentType = 'application/json') {
    let a = document.createElement("a");
    let file = new Blob([content], {type: contentType});
    a.href = URL.createObjectURL(file);
    a.download = fileName;
    a.click();
}
/*****************************************************************************/
static loadFileContents(pFile, funcCallback)
{
    let onFileLoaded = (fileLoadEvent) => {
		funcCallback(fileLoadEvent.target.result, fileLoadEvent.target.fileName);
    };

    let reader = new FileReader();
    reader.onload = onFileLoaded;
    reader.fileName = pFile.name;
    reader.readAsText(pFile);
}
/*****************************************************************************/
static loadFile(funcCallback)
{
	let pTempElem = document.createElement("input");
	pTempElem.type = 'file';
	pTempElem.name = 'files[]';
	pTempElem.onchange = (evt) => {
		let files = evt.target.files;
		CoreUtils.loadFileContents(files[0], funcCallback);
	};
	pTempElem.click();	
}
/*****************************************************************************/
static saveData(filename, blob)
{
	let a = document.createElement("a");
	document.body.appendChild(a);
	a.style = "display: none";

	let url = window.URL.createObjectURL(blob);
	a.href = url;
	a.download = filename;
	a.click();
	window.URL.revokeObjectURL(url);
}
/*****************************************************************************/
static downloadString(filename, text)
{
	let pom = document.createElement('a');
	pom.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
	pom.setAttribute('download', filename);

	if(document.createEvent)
	{
		let event = document.createEvent('MouseEvents');
		event.initEvent('click', true, true);
		pom.dispatchEvent(event);
	}
	else
	{
		pom.click();
	}
}
/*****************************************************************************/
static async forceDownloadFiles(urls) {
    const objectUrls = [];

    try {
        await Promise.all(urls.map(async (url) => {
            try {
                const resp = await fetch(url);
                if (!resp.ok) {
                    throw new Error(`Failed to fetch ${url}: ${resp.status} ${resp.statusText}`);
                }

                const blob = await resp.blob();
                const objectUrl = URL.createObjectURL(blob);
                objectUrls.push(objectUrl);

                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = url.split('/').pop();
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } catch (error) {
                console.error(`Error downloading ${url}:`, error);
                throw error;
            }
        }));
    } finally {
        // Clean up object URLs
        objectUrls.forEach(url => URL.revokeObjectURL(url));
    }
}
/*****************************************************************************/
static queryLocationString()
{
    // This function is anonymous, is executed immediately and
    // the return value is assigned to QueryString!
    let query_string = {};
    let query = window.location.search.substring(1);
    let vars = query.split("&");
    for(let i = 0; i < vars.length; i++)
    {
        let pair = vars[i].split("=");
        // If first entry with this name
        if(typeof query_string[pair[0]] === "undefined")
        {
            query_string[pair[0]] = decodeURIComponent(pair[1]);
            // If second entry with this name
        } else if(typeof query_string[pair[0]] === "string")
        {
            let arr = [query_string[pair[0]], decodeURIComponent(pair[1])];
            query_string[pair[0]] = arr;
            // If third or later entry with this name
        } else
        {
            query_string[pair[0]].push(decodeURIComponent(pair[1]));
        }
    }
    return query_string;
}
/*****************************************************************************/
static areKeysEqual(as, bs)
{
	let asKeys = Object.keys(as);
	let bsKeys = Object.keys(bs);
	if(asKeys.length !== bsKeys.length)
		return false;

	for(let a of asKeys)
	{
		if(bs[a] !== as[a])
			return false;
	}
	return true;
}
/*****************************************************************************/
static multiplyOpacityIntoRGBAStringColor(strColorIn, fOpacityMult)
{
	if(fOpacityMult === 1.0)
		return strColorIn;

	let colValues = CoreUtils.replaceAll(strColorIn, "rgba(", "");
	colValues = CoreUtils.replaceAll(colValues, ")", "");
	colValues = CoreUtils.replaceAll(colValues, ",", " ");
	colValues = CoreUtils.replaceAll(colValues, "  ", " ").split(" ");
	colValues[3] *= fOpacityMult;

	return "rgba(" + colValues[0] + ", " + colValues[1] + ", " + colValues[2] + ", " + colValues[3] + ")";
}
/*****************************************************************************/
static escapeRegExp(str)
{
	return str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
}
/*****************************************************************************/
static replaceAll(str, find, replace)
{
	// Use newer, faster method:
	return str.replaceAll(find, replace);
	// return str.replace(new RegExp(CoreUtils.escapeRegExp(find), 'g'), replace);
}
/*****************************************************************************/
static mode(arrayIn, equalityEpsilon = 0.0, optCompareFunc = null)
{
  let array = arrayIn.slice().sort((x, y) => x - y);

  const defaultCompFunc = (a, b) => Math.abs(a - b) > equalityEpsilon;
  const compareFunc = optCompareFunc ?? defaultCompFunc;

  let bestStreak = 1;
  let bestElem = array[0];
  let currentStreak = 1;
  let currentElem = array[0];

  for(let i = 1; i < array.length; i++)
  {
	  if(compareFunc(array[i - 1], array[i]))
	  {
		  if (currentStreak > bestStreak)
		  {
			  bestStreak = currentStreak;
			  bestElem = currentElem;
		  }

		  currentStreak = 0;
		  currentElem = array[i];
	  }

	  currentStreak++;
  }

  return currentStreak > bestStreak ? currentElem : bestElem;
}
/*****************************************************************************/
static median(values)
{
	values.sort(function(a,b) {
		return a - b;
	});

	if(values.length === 0)
		return 0;

	let half = Math.floor(values.length/2);

	if(values.length % 2)
		return values[half];
	else
		return (values[half - 1] + values[half]) / 2.0;
}
/*****************************************************************************/
static isEmpty(obj)
{
	for(let prop in obj)
	{
		if(obj.hasOwnProperty(prop))
			return false;
	}

	return true && JSON.stringify(obj) === JSON.stringify({});
}
/*****************************************************************************/
static hasClass(element, cls)
{
	return ((' ' + element.className + ' ').indexOf(' ' + cls + ' ') > -1) || (element.className === cls);
}
/*****************************************************************************/
static convertMetersToString(fValue, strTargetUnits)
{
	// The source value is assumed to be in meters
	let strNewVal = fValue;

	if(strTargetUnits === "m")
	{
		strNewVal = fValue.toFixed(2) + "m";
	}
	else if(strTargetUnits === "mm")
	{
		strNewVal = (fValue*1000.0).toFixed(0) + "mm";
	}
	else if(strTargetUnits === "ft")
	{
		let fInches = fValue*39.370;
		let fFeet = Math.floor(fInches*(1.0/12.0));
		fInches -= fFeet*12;

		strNewVal = fFeet + "\' " + Math.floor(fInches) + "\"";
	}
	else
	{
		console.error("Unsupported units specified for conversion: " + strTargetUnits);
	}

	return strNewVal;
}
/*****************************************************************************/
static convertFtInToMeters(fFeet, fInches)
{
	return fFeet*0.305 + fInches*0.025;
}
/**
 * Convert distance in human readable format to meters.
 * @param text Something like `42'2.5"`, `5260mm` or `2.5m`
 * @returns parsed distance converted to meters, or null
 */
static parseDistanceTextMeters(text)
{
	// Match integer or real number. No scientific notation
	const RE_NUMBER = '(?:[0-9]*[.])?[0-9]+';
	// `XX'YY"`. Feet or inches are optional. Inches symbol can be `"` or `''`
	const RE_FEET_INCHES = RegExp(
		`^(?:(${RE_NUMBER})')?(?:(${RE_NUMBER})(?:''|"))?$`
	);
	// `XXm`. Match meters
	const RE_M = RegExp(`^(${RE_NUMBER})\\s*m$`);
	// `XXcm`. Match centimeters
	const RE_CM = RegExp(`^(${RE_NUMBER})\\s*cm$`);
	// `XXmm`. Match millimeters
	const RE_MM = RegExp(`^(${RE_NUMBER})\\s*mm$`);

	const feetInchesMatch = text.match(RE_FEET_INCHES);
	if(feetInchesMatch)
	{
		const [, feet, inches] = feetInchesMatch;
		return (
			parseFloat(feet || '0') * 0.3048 + parseFloat(inches || '0') * 0.0254
		);
	}

	const metersMatch = text.match(RE_M);
	if(metersMatch)
	{
		const [, meters] = metersMatch;
		return parseFloat(meters);
	}

	const centimetersMatch = text.match(RE_CM);
	if(centimetersMatch)
	{
		const [, centimeters] = centimetersMatch;
		return parseFloat(centimeters) / 100;
	}

	const millimetersMatch = text.match(RE_MM);
	if(millimetersMatch)
	{
		const [, millimeters] = millimetersMatch;
		return parseFloat(millimeters) / 1000;
	}

	return null;
}
/*****************************************************************************/
static savePropToObject(obj, attr, pRes)
{
	let bIsArray = (obj[attr] instanceof Array);
	let bIsObject = (obj[attr] instanceof Object);
	let bHasId = bIsObject && (obj[attr] && obj[attr].getId);

	if(bIsArray && obj[attr].length > 0)
	{
		let pFirstObj = obj[attr][0];
		bHasId = pFirstObj && pFirstObj.getId;
	}

	let bIsExportable = (obj[attr] instanceof BABYLON.Vector2)
						|| (obj[attr] instanceof BABYLON.Vector3)
						|| (obj[attr] instanceof BABYLON.Color3)
						|| bHasId;

	//console.log("PIECE ATTR: " + attr + " is obj " + bIsObject + " is vec " + bIsExportable + " test: " +  (obj[attr] && obj[attr].getId));

	if(bIsObject && !bIsExportable)
	{
		//console.log("SKIPPING: " + attr);
		return;
	}

	if(bHasId)
	{
		if(bIsArray)
		{
			// This property is an array of objects with IDs. Export it as such.
			let strNewPropName = attr + "_ids";
			pRes[strNewPropName] = [];
			for(let iSubIdx = 0; iSubIdx < obj[attr].length; iSubIdx++)
				pRes[strNewPropName][iSubIdx] = obj[attr][iSubIdx].getId();
		}
		else
		{
			// This is a property that is an object that has an id. Export the
			// named id prop and do nothing else (these will be used as references
			// on load).
			pRes[attr + "_id"] = obj[attr].getId();
		}
		return;
	}

	if(bIsExportable)
		pRes[attr] = obj[attr].clone();
	else
		pRes[attr] = obj[attr];

	// Add a class name
	if(bIsObject)
	{
		if(obj[attr] instanceof BABYLON.Vector2)
			pRes[attr]['class'] = 'BABYLON.Vector2';
		else if(obj[attr] instanceof BABYLON.Vector3)
			pRes[attr]['class'] = 'BABYLON.Vector3';
		else if(obj[attr] instanceof BABYLON.Color3)
			pRes[attr]['class'] = 'BABYLON.Color3';
		else
			// Note that this doesn't work for functions...
			pRes[attr]['class'] = obj[attr].constructor.name;
	}
}
/*****************************************************************************/
static saveSimplePropsToObject(obj, pSkipProps, pOptExistingObjectOut)
{
	let pRes = pOptExistingObjectOut;
	if(!pRes)
		pRes = {};
	for(let attr in obj)
	{
		if(!obj.hasOwnProperty(attr))
			continue;

		if(pSkipProps && pSkipProps.indexOf(attr) >= 0)
			continue;

		CoreUtils.savePropToObject(obj, attr, pRes);
	}

	return pRes;
}
/*****************************************************************************/
static resolveId(pIdResolver, attr, idValue)
{
	let pFoundObject = undefined;
	if(pIdResolver)
		pFoundObject = pIdResolver.resolveId(attr, idValue);
	// Note that we do not set result to the value passed in
	// if we are null; this is to distinguish between cases
	// where the id is not known or no resolver is set,
	// and cases where we can't find the correct element.
	if(pFoundObject === undefined)
		pFoundObject = idValue;
	return pFoundObject;
}
/*****************************************************************************/
static loadSinglePropFromObject(sourceObj, attr, pIdResolver, targetObj)
{
	let bIsObject = (sourceObj[attr] instanceof Object);

	// First, see if it's an id of something or a list of ids
	if(attr.endsWith('_id'))
	{
		let strNewName = attr.substr(0, attr.length - 3);
		targetObj[strNewName] = CoreUtils.resolveId(pIdResolver, attr, sourceObj[attr]);
		return;
	}
	else if(attr.endsWith('_ids'))
	{
		let strNewName = attr.substr(0, attr.length - 4);
		if(pIdResolver)
			targetObj[strNewName] = pIdResolver.resolveContainer(attr);
		else
			targetObj[strNewName] = [];
		for(let iSubIdx in sourceObj[attr])
			targetObj[strNewName][iSubIdx] = CoreUtils.resolveId(pIdResolver, attr, sourceObj[attr][iSubIdx]);

		return;
	}

	// See if it's an object
	if(sourceObj[attr] && sourceObj[attr]['class'])
	{
		// It's an object
		let pChain = sourceObj[attr]['class'].split('.');
		let pAllocObj = window;
		for(let iChainIdx = 0; iChainIdx < pChain.length; iChainIdx++)
			pAllocObj = pAllocObj[pChain[iChainIdx]];
            // eslint-disable-next-line
		let pObjInst = new pAllocObj();
		CoreUtils.loadPropsFromObject(sourceObj[attr], pObjInst);
		targetObj[attr] = pObjInst;
	}
	else if(!bIsObject)
		targetObj[attr] = sourceObj[attr];
}
/*****************************************************************************/
static loadPropsFromObject(sourceObj, targetObj, pSkipProps, pIdResolver)
{
	if(!sourceObj)
		return;

	for(let attr in sourceObj)
	{
		if(!sourceObj.hasOwnProperty(attr))
			continue;

		if(pSkipProps && pSkipProps.indexOf(attr) >= 0)
			continue;

		CoreUtils.loadSinglePropFromObject(sourceObj, attr, pIdResolver, targetObj);
	}
}
/*****************************************************************************/
static performClick(elemId)
{
	let elem = document.getElementById(elemId);
	if(elem && document.createEvent)
	{
		let evt = document.createEvent("MouseEvents");
		evt.initEvent("click", true, false);
		elem.dispatchEvent(evt);
	}
}
/*****************************************************************************/
static animateObjectProperties(pObject, propValueMap, fDurationSeconds, pCallback, eAnimationType = undefined, bTreatValuesAsAbsolute = false, fps = 60.0)
{
	if(eAnimationType === undefined || eAnimationType === null)
		eAnimationType = CoreUtils.EASE_IN_OUT;

	// 60fps timeout
	const iTimeoutPerFrame = 1000.0/fps;
	let iCurrSteps = 0;
	const iNumSteps = fDurationSeconds*1000.0/iTimeoutPerFrame;

	let origValues = {};
	let finalValues = {};
	let currValues = {};
	for(let propName in propValueMap)
	{
		if(pObject[propName].clone)
			origValues[propName] = pObject[propName].clone();
		else
			origValues[propName] = pObject[propName];

		if(bTreatValuesAsAbsolute)
			finalValues[propName] = propValueMap[propName];
		else
		{
			if(pObject[propName].add)
				finalValues[propName] = pObject[propName].add(propValueMap[propName]);
			else
				finalValues[propName] = pObject[propName] + propValueMap[propName];
		}
	}

	if(iNumSteps <= 0)
	{
		if(pCallback)
			pCallback.call(this, finalValues, 1.0, true, 1, 1);
		return null;
	}

	let pInterval = setInterval(() => {

		let fInterpValue = iCurrSteps/iNumSteps;
		iCurrSteps++;
		if(fInterpValue >= 1.0)
		{
			fInterpValue = 1.0;
			clearInterval(pInterval);
			pInterval = null;
		}

		if(eAnimationType == CoreUtils.EASE_IN_OUT)
			fInterpValue = CoreUtils.convertLinearToEaseInOutInterpolation(fInterpValue);

		for(let propName in propValueMap)
		{
			if(origValues[propName] instanceof BABYLON.Vector3)
				currValues[propName] = BABYLON.Vector3.Lerp(origValues[propName], finalValues[propName], fInterpValue);
			else
				currValues[propName] = CoreUtils.interpolate(fInterpValue, origValues[propName], finalValues[propName]);
		}

		if(pCallback)
			pCallback.call(this, currValues, fInterpValue, !pInterval, iCurrSteps, iNumSteps);

	}, iTimeoutPerFrame);

	return pInterval;
}
/*****************************************************************************/
static animateValues(propValueMap, fDurationSeconds, pCallback, eAnimationType = undefined)
{
	let tempObj = {};
	let finalState = {};
	for(let key in propValueMap)
	{
		tempObj[key] = propValueMap[key][0];
		finalState[key] = propValueMap[key][1];
	}

	return CoreUtils.animateObjectProperties(tempObj, finalState, fDurationSeconds, pCallback, eAnimationType, true);
}
/*****************************************************************************/
static getAllowLogOutput()
{
	// Don't log in test mode to avoid spamming the output
	if(window.process?.env?.JEST_WORKER_ID)
		return false;

	if(EnvConfig.getIsReleaseMode() && EnvConfig.getEnvironment() !== EnvConfig.Environment.DEV)
		return false;

	return true;
}
/*****************************************************************************/
static warn(strValue)
{
	if(!CoreUtils.getAllowLogOutput())
		return;

	if(window.reportLog)
		window.reportLog(strValue);

	console.warn(strValue);
}
/*****************************************************************************/
static log(strValue)
{
	if(!CoreUtils.getAllowLogOutput())
		return;

	if(window.reportLog)
		window.reportLog(strValue);

	console.log(strValue);
}
/*****************************************************************************/
static capitalizeFirstLetterAllWords(string)
{
    return string.replace(/\w\S*/g, CoreUtils.capitalizeFirstLetter);
}
/*****************************************************************************/
static capitalizeFirstLetter(string)
{
	return string.charAt(0).toUpperCase() + string.slice(1);
}
/*****************************************************************************/
static convertLinearToEaseInOutInterpolation(fInterp)
{
	if(fInterp <= 0.5)
		return 2.0*fInterp*fInterp;

	fInterp -= 0.5;
	return 2.0*fInterp*(1.0 - fInterp) + 0.5;
}
/*****************************************************************************/
static interpolate(t, a, b)
{
	return (1 - t) * a + t * b;
}
/*****************************************************************************/
static interpolateCircular(t, a, b)
{
	let t2 = Math.sqrt(1.0 - t*t);
	return t2*a + (1.0 - t2)*b;
}
/*****************************************************************************/
static feetToMeter(feet)
{
    return feet*0.3048;
}
/*****************************************************************************/
static feetToMM(feet)
{
	return CoreUtils.feetToMeter(feet)*1000;
}
/*****************************************************************************/
static meterToFeet(meter)
{
    return meter*3.2808;
}
/*****************************************************************************/
static meterToFeetAndInchesStr(meter, toFixedValue)
{
    let convertedFeet = this.meterToFeet(meter);
    let feet = Math.floor(convertedFeet);
    let inches = (convertedFeet - feet)*12;
    let feetAndInches = `${feet}' ${inches.toFixed(toFixedValue)}"`;
    return feetAndInches;
}
/*****************************************************************************/
static meterToInches(meter)
{
	return meter*39.3701;
}
/*****************************************************************************/
static mmToFeet(mmValue, maxNumberOfDecimals = 2)
{
	const fMult = Math.pow(10, maxNumberOfDecimals);
	const fFeet = mmValue/1000.0*3.2808;
	return Math.round(fFeet*fMult)/fMult;
}
/*****************************************************************************/
static mmToInches(mmValue, maxNumberOfDecimals = 2)
{
	const fMult = Math.pow(10, maxNumberOfDecimals);
	const fInches = mmValue/25.4;
	return Math.round(fInches*fMult)/fMult;
}
/*****************************************************************************/
static inchesToMM(inchesValue, maxNumberOfDecimals = 2)
{
	const fMult = Math.pow(10, maxNumberOfDecimals);
	const fMm = inchesValue*25.4;
	return Math.round(fMm*fMult)/fMult;
}
/*****************************************************************************/
static inchesToMeters(inchesValue, maxNumberOfDecimals = 2)
{
	const fMult = Math.pow(10, maxNumberOfDecimals);
	const fMm = inchesValue*25.4/1000;
	return Math.round(fMm*fMult)/fMult;
}
/*****************************************************************************/
static inchesToFeet(value, inType)
{
	if(inType === 'in')
		return value*0.0833333;
	else
		return value*12;
}
/*****************************************************************************/
static getParameterByName(name, url)
{
    if(!url)
        url = window.location.href;

    name = name.replace(/[\[\]]/g, "\\$&");

    let regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
    let results = regex.exec(url);
    if(!results)
        return null;

    if(!results[2])
        return '';

    return decodeURIComponent(results[2].replace(/\+/g, " "));
}
/*****************************************************************************/
static applyGammaCorrection(colorIn, fGamma = 2.2)
{
    return new BABYLON.Color3(Math.pow(colorIn.r, fGamma), Math.pow(colorIn.g, fGamma), Math.pow(colorIn.b, fGamma));
}
/*****************************************************************************/
static formatNumberWithCommas(number)
{
	 return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
/*****************************************************************************/
static nicefyNameForUI(origName, bRemoveShortAndNumericWords = true)
{
	if(!origName || origName.length === 0)
		return '';

	let res = origName[0];
	for(let idx = 1;  idx < origName.length; idx++)
	{
		if(origName[idx] === ' ' || origName[idx] === '.' || origName[idx] === '-')
		{
			res += '_';
			continue;
		}

		// Insert an underscore whenever we change from lower to upper ase
		const isCurrentUpperCase = origName[idx] == origName[idx].toUpperCase();
		const isCurrentNumber = !isNaN(origName[idx] * 1);
		const isPreviousLowerCase = origName[idx - 1] == origName[idx - 1].toLowerCase();
		const isPreviousNumber = !isNaN(origName[idx - 1] * 1);
		
		if(isCurrentUpperCase && (isPreviousLowerCase || isPreviousNumber)
			&& !(isCurrentNumber && isPreviousNumber) && res[idx - 1] != '_' && origName[idx] != '_')
			res += '_';
		res += origName[idx];
	}

	let splitArray = res.split('_');
	let finalArray = [];
	for(let idx = 0; idx < splitArray.length; idx++)
	{
		let currToken = splitArray[idx];
		if(bRemoveShortAndNumericWords && currToken.length <= 1)
			continue;

		if(bRemoveShortAndNumericWords && /^[0-9]*$/.test(currToken))
			continue;

		if(currToken.indexOf('LOD') === 0)
			continue;

		finalArray.push(currToken);
	}

	let finalName = finalArray.join(' ');
	finalName = CoreUtils.capitalizeFirstLetterAllWords(finalName);

	return finalName;
}
/*****************************************************************************/
static stripUnicode(str)
{
	if(!str)
		return str;

	// Strip out the full Unicode range
	return str.replace(/[\u0080-\uF8FF]/g, '').replace(/\u001F/g, '');
}
/*****************************************************************************/
static escapeXML(str)
{
    return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
/*****************************************************************************/
static setCookie(cName, cValue, expDays)
{
    let date = new Date();
    date.setTime(date.getTime() + (expDays * 24 * 60 * 60 * 1000));
    const expires = "expires=" + date.toUTCString();
    document.cookie = cName + "=" + cValue + "; " + expires + "; path=/";
}
/*****************************************************************************/
static isValidDate(inputDate)
{
  return inputDate instanceof Date && !isNaN(inputDate.valueOf());
}
/*****************************************************************************/
static assert(condition, messageTitle, messageDetails = '', bSoftAssert = false)
{
	if(condition)
		return;
	
	messageTitle = messageTitle || "Assertion failed";
	if(!EnvConfig.getIsReleaseMode() && !bSoftAssert)
	{
		console.error('ASSERT FAILED: ' + messageTitle + ' ' + messageDetails);
		throw new Error(messageTitle + ' ' + messageDetails);
	}
	else
		CoreUtils.reportError('ASSERT FAILED: ' + messageTitle, messageDetails);
}
/*****************************************************************************/
// This is a "soft" assertion statement that dumps a warning to the console
// but does not throw an error. It also shows a stack trace.
// An optional debug function can be passed for the purpose of dumping debug
// data to the console.
//
// Usage:
//	minimal statement, no message: 								softAssert(i === 5)
//	with message string: 										softAssert(i === 5, 'Expected i to be equal to 5')
//	test wrapped by function to avoid execution in release: 	softAssert(() => i === 5)
//	message wrapped in function to avoid execution in release:
//		softAssert(i === 5,() => 'i not equal to 5, note that computeNumber() is equal to ' + computeNumber())
//	using debug function: 										softAssert(i === 5,'i differs from 5',() => logMyDataToConsole())
//
static softAssert(conditionArgument,messageArgument,debugFunc) {
	if (EnvConfig.getIsReleaseMode()) {
		// bail if we are in release mode
		return;
	}
	let condition = (typeof(conditionArgument) === 'boolean') ? conditionArgument : conditionArgument();
	if (condition) {
		return;
	}
	messageArgument = messageArgument || ''; // handle missing message
	let message = (typeof(messageArgument) === 'string') ? messageArgument : messageArgument();
	let err = new Error();
	let stackStr = err.stack.slice(6); // slice away 'Error\n' prefix
	let str = 'Assertion failed: ' + message + '\n' + stackStr;
	console.warn(str);
	if (debugFunc) {
		console.log('*** begin post-assertion debug info ***');
		debugFunc();
		console.log('*** end post-assertion debug info ***');
	}
}
/*****************************************************************************/
static getImageSource(strOrigSrc, thumbWidth, thumbHeight, bIncludeParamsInUrl = true, strOptFillMode='fill', format = null, bUseDevicePixelRatio = true)
{
	let imgSrc = strOrigSrc;
	if(thumbWidth && thumbHeight && imgSrc)
	{
		let strSearchString = 'amazonaws.com/';
		let iEndIdx = strOrigSrc.indexOf(strSearchString);
		if(iEndIdx > 0)
		{
			// In some cases, we actually get URLs that also contain the bucket name - i.e. '/modsy/',
			// '/modsy-dev/', etc.; we intelligently strip those out, not relying on the the BE
			// to be consistent...
			let iModsyIdx = strOrigSrc.indexOf('/modsy', iEndIdx);
			if(iModsyIdx > 0)
			{
				// Find the next slash:
				iEndIdx = strOrigSrc.indexOf('/', iModsyIdx + 1);
				strSearchString = '/';
			}

			let strSuffix = strOrigSrc.substr(iEndIdx + strSearchString.length);
			if(window.getUseImgIx && window.getUseImgIx())
				imgSrc = EnvConfig.getImgIxBaseUrl();
			else
				imgSrc = EnvConfig.getImgBaseUrl();
			imgSrc += strSuffix;
		}

		if(bUseDevicePixelRatio)
		{
			thumbWidth *= window.devicePixelRatio;
			thumbHeight *= window.devicePixelRatio; 
		}

		if(bIncludeParamsInUrl)
		{
			imgSrc += "?w=" + thumbWidth + "&h=" + thumbHeight;
			imgSrc += "&fit=" + strOptFillMode + "&fill=solid&fill-color=ffffff";
			if(format)
				imgSrc += '&auto=compress&fm=' + format;
			else
				imgSrc += '&auto=format%2Ccompress&fm=jpg';				
		}
	}
	return imgSrc;
}
/*****************************************************************************/
static areArraysEqual(a, b, sort = false)
{
	if(a === b)
		return true;

	if(a == null || b == null)
		return false;

	if(a == undefined || b == undefined)
		return false;

	if(a.length !== b.length)
		return false;

	let aCopy = a;
	let bCopy = b;
	if (sort) {
		aCopy = [...a];
		aCopy.sort();

		bCopy = [...b];
		bCopy.sort();
	}

	for(let i = 0; i < a.length; ++i) {
		if(aCopy[i] !== bCopy[i])
			return false;
	}

	return true;
}
/*****************************************************************************/
/**
    * Floating point number checking closeness to the zero according to sensitivity
    * @param number: that is wanted to be tested
    * @param sensitivity: number. optional
    * @returns bool: whether it is zero
    */
static isZero(number, sensitivity = null) {
    return Math.abs(number) < (sensitivity ? sensitivity : 1.e-12);
}
/*****************************************************************************/
/**
    * Report error.
    * If 'window' object is defined, and contains a report function, then it will be called.
    * Otherwise it will print an error message to the console.
    * @param tag Error's tag.
    * @param comment Error's comment.
    */
static reportError(tag, comment) {
	// We don't want to crash if there's no error handler - this library is generic and
	// is now used by at least four projects, not all of which need to report errors remotely.
	if(window.reportError)
		window.reportError(tag, comment);
	else
	    console.error(`Failed reporting error: [${tag}] ${comment}`);        
}
/*****************************************************************************/
static normalizeProbabilityList(probList, probFieldName)
{
	let fTotalWeight = 0.0;
	for(let idx in probList)
		fTotalWeight += probList[idx][probFieldName];

	// Special case - if all of our probs are zero.
	if(fTotalWeight < 1e-3)
	{
		for(let idx in probList)
			probList[idx][probFieldName] = 1.0;
		fTotalWeight = probList.length;
	}

	for(let idx in probList)
		probList[idx][probFieldName] /= fTotalWeight;
}
/*****************************************************************************/
static pickFromProbabilityList(probList, probFieldName, bDoAutoNormalize = false, fProbability = undefined)
{
	if(bDoAutoNormalize)
		Utils.normalizeProbabilityList(probList, probFieldName);

	const fOurProb = (fProbability === undefined) ? Math.random() : fProbability;
	let fAccumProb = 0.0;
	let iCurrPos = 0;
	while(fOurProb > fAccumProb && iCurrPos < probList.length)
	{
		fAccumProb += probList[iCurrPos][probFieldName];
		iCurrPos++;
	}

	if(iCurrPos > 0)
		iCurrPos--;
	if(iCurrPos >= probList.length)
		iCurrPos = probList.length - 1;

	return iCurrPos;
}
/*****************************************************************************
 // This is potentially a way to get around the size limit in FileReader when
 // it starts returning an empty string with no error (around 400Mb binary data).
 // However, it needs work since this crashes, too, with array size that's too
 // large for JS to handle. So we'd have to process it in chunks, since I think
 // the JS max string lengths can actually accommodate the final result.
static async convertBlobToBase64Slow(blob)
{
	const buffer = await blob.arrayBuffer(); 
	const view = new Uint8Array(buffer);
	const binArray = Array.from(view, (byte) => String.fromCharCode(byte));
	const binString = binArray.join("");
	const theImage = btoa(binString);
	return `data:${blob.type};base64,` + theImage;
}
/*****************************************************************************/
static convertBlobToBase64(blob, callback)
{
	const reader = new FileReader();
	reader.onloadend = () => {
		const base64data = reader.result;
		callback(base64data);
	}
	reader.readAsDataURL(blob); 
}
/*****************************************************************************/
static convertBase64toBlob(b64Data, contentType = '', sliceSize = 512)
{

	const CUT_STRING = 'base64,';
	let iCutIdx = b64Data.indexOf(CUT_STRING);
	if(iCutIdx >= 0)
		b64Data = b64Data.substr(iCutIdx + CUT_STRING.length);

	const byteCharacters = atob(b64Data);
	const byteArrays = [];

	for(let offset = 0; offset < byteCharacters.length; offset += sliceSize)
	{
		const slice = byteCharacters.slice(offset, offset + sliceSize);

		const byteNumbers = new Array(slice.length);
		for(let i = 0; i < slice.length; i++)
			byteNumbers[i] = slice.charCodeAt(i);

		const byteArray = new Uint8Array(byteNumbers);
		byteArrays.push(byteArray);
	}

	return  new Blob(byteArrays, {type: contentType});
}
/*****************************************************************************/
static incrementNumericSuffix(strName, optSuffixPrefix = '')
{
	let idx;
	const firstCharCode = "0".charCodeAt(0);
	const lastCharCode = "9".charCodeAt(0);
	for(idx = strName.length - 1; idx >= 0; idx--)
	{
		const charCode = strName.charCodeAt(idx);
		if(charCode < firstCharCode || charCode > lastCharCode)
			break;
	}

	if(idx >= strName.length - 1)
		return strName + optSuffixPrefix + '2';

	// We have a numeric suffix:
	let iNewSuffix = parseInt(strName.substr(idx + 1)) + 1;
	return strName.substr(0, idx + 1) + iNewSuffix;
}
/*****************************************************************************/
static splitFullFilename(strFullPath)
{
	const path = strFullPath.substring(0, strFullPath.lastIndexOf("/") + 1);
	const filename = strFullPath.split("/").pop();

	return {path, filename};
}
/*****************************************************************************/
static uploadFileToS3(strPath, pBlob, strContentType, pSuccessCallback, pErrorCallback)
{
	let strUrlTest = EnvConfig.getWarehouseApiUrl('s3-presigned-upload-url') + '?key=' + strPath + '&content-type=' + strContentType + '&expires=1800';
	$.ajax({
		url: strUrlTest,
		context: this,
		method: "GET",
		success: (data) =>
		{
			const ignoreKeys = { 'success_action_status': true };
			const formData = new FormData();

			for(let k in data.fields) {
				if(data.fields.hasOwnProperty(k) && !ignoreKeys.hasOwnProperty(k))
					formData.append(k, data.fields[k]);
			}

			// Note that the name has to be `file`
			formData.append('file', pBlob);

			// POST to the presigned URL
            const fileS3Url = `${data.url}${strPath}`;
			CoreUtils.log(`Uploading file to ${fileS3Url}`);
			$.ajax({
				url: data.url,
				data: formData,
				processData: false,
				contentType: false,
				type: 'POST',
				success: (uploadResponse) => 
				{
                    if(pSuccessCallback)
                        pSuccessCallback.call(this, fileS3Url);
				},
				error: (xhr, textStatus, errorThrown) =>
				{
					if(pErrorCallback)
                        pErrorCallback.call(this, fileS3Url, "Upload failed: " + textStatus + " " + xhr.status + " " + xhr.responseText);
				}
			});
		},
		error: (xhr, textStatus, errorThrown) =>
		{
			if(pErrorCallback)
				pErrorCallback.call(this, '', "Getting presigned url failed: " + textStatus + " " + xhr.status + " " + xhr.responseText);
		}
	});
}
/*****************************************************************************/
static getS3RelativePath(url) {
    const urlObj = new URL(url);
    return urlObj.pathname.split('/').slice(2).join('/');
}
/*****************************************************************************/
static getTimestampedUUID() {
	const prefix = (0xFFFFFFFF + CoreUtils.murmurhash3(CoreUtils.uuidv4())).toString(16).slice(-9);
	const ts = new Date().toISOString().replace(/[-:.TZ]/g, '_').replace('T', '_').slice(0, -5);
	return prefix + '_' + ts;
}
/*****************************************************************************/
static uuidv4() {
	let uuid = '';
	for (let i = 0; i < 32; i++) {
	  if (i === 8 || i === 12 || i === 16 || i === 20) {
		uuid += '-';
	  }
	  uuid += Math.floor(Math.random() * 16).toString(16);
	}
	return uuid;
}
/*****************************************************************************/
static murmurhash3(key, seed = 0) {
	let h1 = seed;
	const c1 = 0xcc9e2d51;
	const c2 = 0x1b873593;
	const len = key.length;
  
	for (let i = 0; i < len; i += 4) {
	  let k1 = ((key.charCodeAt(i) & 0xff)) |
			   ((key.charCodeAt(i + 1) & 0xff) << 8) |
			   ((key.charCodeAt(i + 2) & 0xff) << 16) |
			   ((key.charCodeAt(i + 3) & 0xff) << 24);
  
	  k1 = (k1 * c1) >>> 0;
	  k1 = ((k1 << 15) | (k1 >>> 17)) >>> 0;
	  k1 = (k1 * c2) >>> 0;
  
	  h1 ^= k1;
	  h1 = ((h1 << 13) | (h1 >>> 19)) >>> 0;
	  h1 = (h1 * 5 + 0xe6546b64) >>> 0;
	}
  
	let remaining = len & 3;
	if (remaining > 0) {
	  let k1 = 0;
	  switch (remaining) {
		case 3:
		  k1 ^= (key.charCodeAt(len - 2) & 0xff) << 16;
		  // eslint-disable-next-line no-fallthrough
		case 2:
		  k1 ^= (key.charCodeAt(len - 3) & 0xff) << 8;
		  // eslint-disable-next-line no-fallthrough
		case 1:
		  k1 ^= (key.charCodeAt(len - 4) & 0xff);
		  k1 = (k1 * c1) >>> 0;
		  k1 = ((k1 << 15) | (k1 >>> 17)) >>> 0;
		  k1 = (k1 * c2) >>> 0;
		  h1 ^= k1;
	  }
	}
  
	h1 ^= len;
	h1 ^= h1 >>> 16;
	h1 = (h1 * 0x85ebca6b) >>> 0;
	h1 ^= h1 >>> 13;
	h1 = (h1 * 0xc2b2ae35) >>> 0;
	h1 ^= h1 >>> 16;
  
	return h1;
}
/*****************************************************************************/
static ensurePowerOfTwo(numberIn)
{
	const iPower = Math.ceil(Math.log2(numberIn));
	return  Math.pow(2, iPower);
}
/*****************************************************************************/
static getImageResizingUrl(imgUrl, width = 100, height = 100, format = 'jpg') {
	if (!imgUrl || imgUrl.length === 0) {
		return '';
	}

	return `${imgUrl}?w=${width}&h=${height}&fm=${format}`;
}
/*****************************************************************************/
static makeArrayUnique(objectArray, propertyName)
{
	if(propertyName)
		return [...new Map(objectArray.map(item => [item[propertyName], item])).values()];
	else
		return [...new Set(objectArray).values()];
}
/*****************************************************************************/
static stringifyNumber(n)
{
	const special = ['zeroth','first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth', 'thirteenth', 'fourteenth', 'fifteenth', 'sixteenth', 'seventeenth', 'eighteenth', 'nineteenth'];
	const deca = ['twent', 'thirt', 'fort', 'fift', 'sixt', 'sevent', 'eight', 'ninet'];

	let res;
	if(n < 20)
		res = special[n];
	else if(n%10 === 0)
		res = deca[Math.floor(n/10)-2] + 'ieth';
	else
		res = deca[Math.floor(n/10)-2] + 'y-' + special[n%10];
	return CoreUtils.capitalizeFirstLetter(res);
}
/*****************************************************************************/
static processPromisesSequentially(promiseFuncs) {
    let promiseChain = Promise.resolve();

    for (const promiseFunc of promiseFuncs) {
        promiseChain = promiseChain.then(promiseFunc);
    }

    return promiseChain;
}
/*****************************************************************************/
static getIntersection(set1, set2) {
    const ans = new Set(); 
    for (let i of set2) { 
        if (set1.has(i)) { 
            ans.add(i); 
        } 
    } 
    return ans; 
} 
/*****************************************************************************/
static matchAllWordsAndHighlight(query, record, bReturnResultOnEmptySearch = false)
{
	query = query.trim();

	let lowerCaseQuery = query.toLowerCase();
	if(lowerCaseQuery.length === 0)
		return bReturnResultOnEmptySearch ? record : null;

	// All words must match
	let tokens = lowerCaseQuery.split(' ');
	let lowerCaseRecord = record.toLowerCase();
	for(let token of tokens)
	{
		let tokenStart = lowerCaseRecord.indexOf(token);
		if(tokenStart < 0)
			return null;
	}

	tokens = query.split(' ');
	for(let token of tokens)
	{
		let re = new RegExp(token, 'gi');
		record = record.replaceAll(re, `<mark>${token}</mark>`);
	}
	return record;
}
/*****************************************************************************/
static mergeObjectsOfArrays(objListIn)
{
	if(!objListIn)
		return {};

	let objList = objListIn.filter(item => !!item);
	if(objList.length === 0)
		return {};

	let keys = Object.keys(objList[0]);
	let finalObj = objList.reduce((accumulator, currValue) => {
		keys.forEach(currKey => {
			if(!currValue[currKey])
				return;
			let assetIds = currValue[currKey].filter(id => id !== null && id !== undefined);
			accumulator[currKey] = accumulator[currKey].concat(assetIds);
		});
		return accumulator;
	});

	keys.forEach(key => {
		finalObj[key] = CoreUtils.makeArrayUnique(finalObj[key]);
	});

	return finalObj;
}
/*****************************************************************************/
static isEnumValue(value, enumObj): boolean
{
	const enumValues = Object.values(enumObj).filter(v => typeof v === 'number');
	return enumValues.includes(value);
}
/*****************************************************************************/
static getNiceStringFromMilliseconds(timeInMs)
{
	let iNumHours = 0;
	let iNumMinutes = timeInMs/(1000*60);
	if(iNumMinutes >= 60) {
		iNumHours = Math.floor(iNumMinutes/60);
		iNumMinutes = Math.round(iNumMinutes - iNumHours*60);
	}
	else
		iNumMinutes = Math.round(iNumMinutes);

	let hoursSuffix = iNumHours === 1 ? '' : 's';
	let minutesSuffix = iNumMinutes === 1 ? '' : 's';

	if(iNumHours > 0 && iNumMinutes > 0)
		return `${iNumHours} hour${hoursSuffix} and ${iNumMinutes} minute${minutesSuffix}`;
	else if(iNumHours > 0)
		return `${iNumHours} hour${hoursSuffix}`; 
	else
		return `${iNumMinutes} minute${minutesSuffix}`;
}
/*****************************************************************************/
}
